import { useCallback, useState } from 'react';
import type { ToastData } from '../components/Toast/ToastContainer';

export interface UseToastReturn {
  toasts: ToastData[];
  showToast: (toast: Omit<ToastData, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAllToasts: () => void;
}

export function useToast(): UseToastReturn {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const generateId = useCallback(() => {
    return `toast_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  const showToast = useCallback(
    (toastData: Omit<ToastData, 'id'>) => {
      const id = generateId();
      const newToast: ToastData = {
        ...toastData,
        id,
        duration: toastData.duration || 5000,
      };

      setToasts((prev) => [...prev, newToast]);
      return id;
    },
    [generateId],
  );

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearAllToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return {
    toasts,
    showToast,
    removeToast,
    clearAllToasts,
  };
}

type ToastShowHandler = (title: string, message?: string, action?: ToastData['action']) => string;

export interface ToastHelpers extends UseToastReturn {
  showSuccess: ToastShowHandler;
  showError: ToastShowHandler;
  showWarning: ToastShowHandler;
  showInfo: ToastShowHandler;
}

// Convenience functions for common toast types
export function useToastHelpers(): ToastHelpers {
  const { showToast, ...rest } = useToast();

  const showSuccess = useCallback(
    (title: string, message?: string, action?: ToastData['action']) => {
      return showToast({ type: 'success', title, message, action });
    },
    [showToast],
  );

  const showError = useCallback(
    (title: string, message?: string, action?: ToastData['action']) => {
      return showToast({ type: 'error', title, message, action, duration: 7000 });
    },
    [showToast],
  );

  const showWarning = useCallback(
    (title: string, message?: string, action?: ToastData['action']) => {
      return showToast({ type: 'warning', title, message, action });
    },
    [showToast],
  );

  const showInfo = useCallback(
    (title: string, message?: string, action?: ToastData['action']) => {
      return showToast({ type: 'info', title, message, action });
    },
    [showToast],
  );

  return {
    showToast,
    showSuccess,
    showError,
    showWarning,
    showInfo,
    ...rest,
  };
}
